import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import validator from 'validator';
import { fetchCategoryPricingStats, fetchEventFiscalSerials } from '../redux/slices/eventsSlice';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import NoData from './NoData';
import WarningDialog from './WarningDialog';
import api from '../api';

const CATEGORY_DESCRIPTION_MAX_LENGTH = 160;
const CATEGORY_DESCRIPTION_PREVIEW_LENGTH = 15;

const CategoryPricingHeader = ({ role }) => {
  const { t } = useTranslation();

  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: 50 }} />
        <TableCell align="left">{t('Category')}</TableCell>
        <TableCell align="left">{t('Description')}</TableCell>
        <TableCell align="right">{t('Price')}</TableCell>
        <TableCell align="right">{t('Discount')}</TableCell>
        {role === 'organizer' && <TableCell align="right">{t('Online')}</TableCell>}
        <TableCell align="right">{role === 'organizer' ? t('Cashier') : t('Booked')}</TableCell>
        {role === 'organizer' && <TableCell align="right">{t('Organizer')}</TableCell>}
        <TableCell align="right">{t('Available')}</TableCell>
        {/*role === 'organizer' && <TableCell align="right">Not For Sale</TableCell>*/}
        <TableCell align="right">{t('Capacity')}</TableCell>
        {role === 'organizer' && <TableCell align="right">{t('Revenue Online')}</TableCell>}
        <TableCell align="right">{role === 'organizer' ? t('Revenue Cashier') : t('Revenue')}</TableCell>
        {role === 'organizer' && <TableCell align="right">{t('Actions')}</TableCell>}
      </TableRow>
    </TableHead>
  );
};

const CategoryPricingFooter = ({
  role,
  booked,
  booked_organizer,
  booked_cashier,
  available,
  not_for_sale,
  capacity,
  revenue_cashier,
  revenue,
}) => {
  const cellStyle = {
    whiteSpace: 'nowrap',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: (theme) => theme.palette.text.primary,
  };
  const { t } = useTranslation();

  return (
    <TableFooter>
      <TableRow sx={{ 'td, th': { borderBottom: 0 } }}>
        <TableCell sx={{ ...cellStyle, width: 50 }} />
        <TableCell colSpan={4} sx={cellStyle}>
          {t('Total')}
        </TableCell>
        {role === 'organizer' && (
          <TableCell align="right" sx={cellStyle}>
            {booked}
          </TableCell>
        )}
        <TableCell align="right" sx={cellStyle}>
          {booked_cashier}
        </TableCell>
        {role === 'organizer' && (
          <TableCell align="right" sx={cellStyle}>
            {booked_organizer}
          </TableCell>
        )}
        <TableCell align="right" sx={cellStyle}>
          {available}
        </TableCell>
        {/*role === 'organizer' && (
          <TableCell align="right" sx={cellStyle}>
            {not_for_sale}
          </TableCell>
        )*/}
        <TableCell align="right" sx={cellStyle}>
          {capacity}
        </TableCell>
        {role === 'organizer' && (
          <TableCell align="right" sx={cellStyle}>
            {revenue.toFixed(2) + ' Lei'}
          </TableCell>
        )}
        <TableCell align="right" sx={cellStyle}>
          {revenue_cashier.toFixed(2) + ' Lei'}
        </TableCell>
      </TableRow>
    </TableFooter>
  );
};

function Row(props) {
  const { t } = useTranslation();

  const theme = useTheme();
  const { row, role, eventId, history } = props;
  const [open, setOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDiscountError, setIsDiscountError] = useState(false);
  const [variablePrice, setVariablePrice] = useState(row.price);
  const [variableDescription, setVariableDescription] = useState(row.description || '');
  const [warningOpen, setWarningOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const originalDiscount = useMemo(() => row.discount * 100, [row.discount]);
  const originalDescription = useMemo(() => row.description || '', [row.description]);
  const priceWithDiscount = useMemo(() => row.price - row.discount * row.price, [row.discount, row.price]);
  const [variableDiscount, setVariableDiscount] = useState(originalDiscount);

  const dispatch = useDispatch();

  const isUpdateDisabled = useMemo(
    () =>
      isError ||
      isDiscountError ||
      (parseFloat(variablePrice) === parseFloat(row.price) &&
        variableDiscount === originalDiscount &&
        variableDescription === originalDescription),
    [
      isError,
      isDiscountError,
      variablePrice,
      row.price,
      variableDiscount,
      originalDiscount,
      variableDescription,
      originalDescription,
    ]
  );

  const toggleOpenWarning = () => {
    setWarningOpen(!warningOpen);
  };

  const updatePrice = () => {
    setIsLoading(true);
    api
      .put(`/categoryPricings/${row.id}`, {
        price: variablePrice,
        discount: variableDiscount,
        description: variableDescription,
      })
      .then(() => {
        setIsEditModalOpen(false);
        setIsLoading(false);
        toggleOpenWarning();
        dispatch(fetchCategoryPricingStats({ eventId }));
        dispatch(fetchEventFiscalSerials({ eventId }));
        toast.success(t('Price updated successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
      })
      .catch(() => {
        setIsLoading(false);
        setVariablePrice(row.price);
        setVariableDiscount(originalDiscount);
        setVariableDescription(originalDescription);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  const onPriceChange = (event) => {
    setIsError(false);
    const number = event.target.value;
    setVariablePrice(number);
    if (!(validator.isDecimal(number, { decimal_digits: '0,2' }) && number >= 0)) {
      setIsError(true);
    }
  };

  const onDiscountChange = (event) => {
    setIsDiscountError(false);
    const number = event.target.value;
    setVariableDiscount(number);
    if (number === '' || !(Number.isInteger(+number) && number >= 0 && number <= 100)) {
      setIsDiscountError(true);
    }
  };

  const onEdit = () => {
    setVariablePrice(row.price);
    setVariableDiscount(originalDiscount);
    setVariableDescription(originalDescription);
    setIsDiscountError(false);
    setIsError(false);
    setIsEditModalOpen(true);
  };

  const onCloseEditModal = () => {
    setIsEditModalOpen(false);
    setVariablePrice(row.price);
    setVariableDiscount(originalDiscount);
    setVariableDescription(originalDescription);
    setIsDiscountError(false);
    setIsError(false);
  };

  const categoryDescription = row.description || '';
  const shortDescription =
    categoryDescription.length > CATEGORY_DESCRIPTION_PREVIEW_LENGTH
      ? `${categoryDescription.slice(0, CATEGORY_DESCRIPTION_PREVIEW_LENGTH)}...`
      : categoryDescription;

  return (
    <>
      <TableRow
        sx={{
          '& > td': { whiteSpace: 'nowrap', borderBottom: 'unset' },
          '&:nth-of-type(4n+1)': (theme) => ({
            backgroundColor: theme.palette.action.hover,
          }),
        }}
      >
        <TableCell sx={{ borderLeft: `5px solid ${row.color}` }}>
          <IconButton disabled={!history.length} aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ display: 'inline-flex' }}>
          {row.name}
          {!row.is_default_type && <Chip sx={{ marginLeft: 1 }} size="small" label={row.type_label} />}
        </TableCell>
        <TableCell align="left">
          {categoryDescription ? (
            <Tooltip title={categoryDescription}>
              <span>{shortDescription}</span>
            </Tooltip>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align="right">
          {originalDiscount !== 0 ? (
            <Stack>
              <span style={{ textDecoration: 'line-through', fontSize: '0.70rem' }}>{row.price + ' Lei'}</span>
              <span style={{ color: theme.palette.error.main }}>{priceWithDiscount + ' Lei'}</span>
            </Stack>
          ) : (
            row.price + ' Lei'
          )}
        </TableCell>
        <TableCell align="right">{row.discount * 100 + '%'}</TableCell>
        {role === 'organizer' && <TableCell align="right">{row.booked}</TableCell>}
        <TableCell align="right">{row.booked_cashier}</TableCell>
        {role === 'organizer' && <TableCell align="right">{row.booked_organizer}</TableCell>}
        <TableCell align="right">{row.available}</TableCell>
        {/*role === 'organizer' && <TableCell align="right">{row.not_for_sale}</TableCell>*/}
        <TableCell align="right">{row.capacity}</TableCell>
        {role === 'organizer' && <TableCell align="right">{row.revenue.toFixed(2) + ' Lei'}</TableCell>}
        <TableCell align="right">{row.revenue_cashier.toFixed(2) + ' Lei'}</TableCell>
        {role === 'organizer' && (
          <TableCell align="right">
            <IconButton onClick={onEdit} size="small">
              <EditIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      <TableRow sx={{ '&:last-child > td, &:last-child > th': { border: 0 } }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 8, mr: 8, mb: 4, mt: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t('History')}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">{t('Price')}</TableCell>
                    <TableCell align="right">{t('Discount')}</TableCell>
                    {role === 'organizer' && <TableCell align="right">{t('Online')}</TableCell>}
                    <TableCell align="right">{role === 'organizer' ? t('Cashier') : t('Booked')}</TableCell>
                    {role === 'organizer' && <TableCell align="right">{t('Organizer')}</TableCell>}
                    {role === 'organizer' && <TableCell align="right">{t('Revenue Online')}</TableCell>}
                    <TableCell align="right">{role === 'organizer' ? t('Revenue Cashier') : t('Revenue')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((category) => (
                    <TableRow sx={{ '& > td': { whiteSpace: 'nowrap' } }} key={category.id}>
                      <TableCell align="left">{category.price + ' Lei'}</TableCell>
                      <TableCell align="right">{category.discount * 100 + ' %'}</TableCell>
                      {role === 'organizer' && <TableCell align="right">{category.booked}</TableCell>}
                      <TableCell align="right">{category.booked_cashier}</TableCell>
                      {role === 'organizer' && <TableCell align="right">{category.booked_organizer}</TableCell>}
                      {role === 'organizer' && (
                        <TableCell align="right">{category.revenue.toFixed(2) + ' Lei'}</TableCell>
                      )}
                      <TableCell align="right">{category.revenue_cashier.toFixed(2) + ' Lei'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <WarningDialog
        title={t(`Warning`)}
        content={t('Are you sure you want to update pricing?')}
        loading={isLoading}
        open={warningOpen}
        onCancel={toggleOpenWarning}
        onConfirm={updatePrice}
      />
      <Dialog open={isEditModalOpen} onClose={onCloseEditModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('Edit Category Pricing')}{' '}
          <IconButton
            aria-label="close"
            onClick={onCloseEditModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('Category Name')} value={row.name} disabled fullWidth />
            <TextField
              label={t('Price')}
              value={variablePrice}
              onChange={onPriceChange}
              error={isError}
              helperText={isError ? t('Invalid price') : ' '}
              fullWidth
            />
            <TextField
              label={t('Discount')}
              value={variableDiscount}
              onChange={onDiscountChange}
              error={isDiscountError}
              helperText={isDiscountError ? t('Invalid discount') : t('Discount is in percents (0-100)')}
              fullWidth
            />
            <TextField
              label={t('Category description')}
              value={variableDescription}
              onChange={(event) => setVariableDescription(event.target.value)}
              helperText={t('Characters: {{count}}/{{max}}', {
                count: variableDescription.length,
                max: CATEGORY_DESCRIPTION_MAX_LENGTH,
              })}
              inputProps={{ maxLength: CATEGORY_DESCRIPTION_MAX_LENGTH }}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={toggleOpenWarning} disabled={isUpdateDisabled}>
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function CategoryPricingStats({ eventId, role, stats, loading = false }) {
  const { t } = useTranslation();

  const { data } = stats;
  const currentPrices = useMemo(() => data.filter(({ status }) => status === 'ACTIVE'), [data]);
  const historicalPricesById = useMemo(
    () =>
      data
        .filter(({ status }) => status === 'ARCHIVED')
        .reduce(
          (acc, value) => ({
            ...acc,
            [`${value.categories_id}-${value.type_id}`]: [
              ...(acc[`${value.categories_id}-${value.type_id}`] || []),
              value,
            ],
          }),
          {}
        ),
    [data]
  );
  return (
    <>
      <Typography gutterBottom variant="h5">
        {t('Category Pricings')}
      </Typography>
      {loading ? (
        <Paper sx={{ mt: 2, mb: 2, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress aria-label={t('Loading...')} />
        </Paper>
      ) : data.length ? (
        <TableContainer sx={{ mt: 2 }} component={Paper}>
          <Table>
            <CategoryPricingHeader role={role} />
            <TableBody>
              {currentPrices.map((stat) => (
                <Row
                  key={stat.id}
                  role={role}
                  row={stat}
                  history={historicalPricesById[`${stat.categories_id}-${stat.type_id}`] || []}
                  eventId={eventId}
                />
              ))}
            </TableBody>
            <CategoryPricingFooter role={role} {...stats} />
          </Table>
        </TableContainer>
      ) : (
        <NoData />
      )}
    </>
  );
}
